import React, { useState, useRef, useEffect } from "react";
import { Button, useTheme } from "@mui/material";
import { XMarkIcon } from '@heroicons/react/20/solid';
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { formatFileSize, MAX_FILE_LIMIT } from "../../utils/fileUploadUtils";
import useFocusTrap from "../../hooks/useFocusTrap";
import { validateEmail } from "../auth/validationHelpers";

// Identifies a file in the bulk list (files are de-duplicated on these fields)
export const getUploadFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

export const getEmailError = (email) => validateEmail((email || "").trim());

const inputClassName =
  "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

const EmailInput = ({ id, value, onChange, theme, autoFocus = false }) => {
  const [touched, setTouched] = useState(false);
  const error = touched || value ? getEmailError(value) : "";
  return (
    <>
      <input
        id={id}
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="name@example.com"
        className={inputClassName}
        style={{
          borderColor: error ? theme.palette.error.main : theme.palette.secondary.v4,
          color: theme.palette.text.main,
        }}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        autoFocus={autoFocus}
      />
      {error && (
        <p id={`${id}-error`} className="text-xs mt-1 text-red-600">
          {error}
        </p>
      )}
    </>
  );
};

const TabbedUploadModal = ({
  isOpen,
  onClose,
  // Single upload props
  candidateName,
  setCandidateName,
  candidateEmail,
  setCandidateEmail,
  selectedFile,
  setSelectedFile,
  isUploadingCandidate,
  addCandidateError,
  handleAddCandidateFileChange,
  handleAddCandidateFileDrop,
  handleAddCandidateFileDragOver,
  handleUploadAndContinue,
  // Bulk upload props
  files,
  bulkDetails,
  handleBulkDetailChange,
  handleFileChange,
  handleRemoveFile,
  isProcessing,
  candidateModalError,
  isEvaluateButtonEnabled,
  handleUpload,
  // Optional error clearing callbacks
  onClearBulkError,
  onClearSingleError,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("single");
  const addCandidateFileInputRef = useRef(null);
  const modalRef = useFocusTrap(isOpen, onClose);
  const prevTabRef = useRef(activeTab);

  // Clear errors when switching tabs
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      if (prevTabRef.current === "bulk" && onClearBulkError) {
        onClearBulkError();
      } else if (prevTabRef.current === "single" && onClearSingleError) {
        onClearSingleError();
      }
      prevTabRef.current = activeTab;
    }
  }, [activeTab, onClearBulkError, onClearSingleError]);

  if (!isOpen) return null;

  const handleClose = () => {
    setActiveTab("single");
    prevTabRef.current = "single";
    onClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const isSingleComplete = !!selectedFile && !getEmailError(candidateEmail);
  const isBulkComplete = files.every((file) => {
    const details = bulkDetails[getUploadFileKey(file)] || {};
    return (details.name || "").trim() && !getEmailError(details.email);
  });

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
      <div className="w-full flex justify-center items-start mt-[70px]">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[calc(100vh-140px)] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tabbed-upload-title"
        >
          {/* Modal Header */}
          <div
            className="modal-header flex justify-between items-center py-4 px-5 border-b"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.secondary.v4,
            }}
          >
            <h2 id="tabbed-upload-title" className="font-semibold">Add Candidates</h2>
            <button
              onClick={handleClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b" style={{ borderColor: theme.palette.secondary.v4 }}>
            <button
              onClick={() => handleTabChange("single")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "single" ? "border-b-2" : ""
              }`}
              style={{
                color: activeTab === "single" ? theme.palette.info.main : theme.palette.text.secondary,
                borderBottomColor: activeTab === "single" ? theme.palette.info.main : "transparent",
                backgroundColor: theme.palette.primary.main,
              }}
            >
              Single Upload
            </button>
            <button
              onClick={() => handleTabChange("bulk")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "bulk" ? "border-b-2" : ""
              }`}
              style={{
                color: activeTab === "bulk" ? theme.palette.info.main : theme.palette.text.secondary,
                borderBottomColor: activeTab === "bulk" ? theme.palette.info.main : "transparent",
                backgroundColor: theme.palette.primary.main,
              }}
            >
              Bulk Upload
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex flex-col flex-grow overflow-hidden">
            {activeTab === "single" ? (
              <SingleUploadTab
                candidateName={candidateName}
                setCandidateName={setCandidateName}
                candidateEmail={candidateEmail}
                setCandidateEmail={setCandidateEmail}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                addCandidateFileInputRef={addCandidateFileInputRef}
                handleAddCandidateFileChange={handleAddCandidateFileChange}
                handleAddCandidateFileDrop={handleAddCandidateFileDrop}
                handleAddCandidateFileDragOver={handleAddCandidateFileDragOver}
                theme={theme}
              />
            ) : (
              <BulkUploadTab
                files={files}
                bulkDetails={bulkDetails}
                handleBulkDetailChange={handleBulkDetailChange}
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
                theme={theme}
              />
            )}
          </div>

          {/* Error Messages */}
          {((activeTab === "single" && addCandidateError) || (activeTab === "bulk" && candidateModalError)) && (
            <div className="px-5 pb-0">
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded mb-2">
                {activeTab === "single" ? addCandidateError : candidateModalError}
              </div>
            </div>
          )}

          {/* Modal Footer */}
          <div className="w-full py-3 px-5 flex justify-end border-t gap-2" style={{ borderColor: theme.palette.secondary.v4 }}>
            <Button
              onClick={activeTab === "single" ? handleUploadAndContinue : handleUpload}
              disabled={
                activeTab === "single"
                  ? (!candidateName.trim() || !candidateEmail.trim() || !selectedFile || isUploadingCandidate)
                  : (!isEvaluateButtonEnabled || isProcessing || files.some((file) => {
                      const detail = bulkDetails?.[`${file.name}-${file.size}-${file.lastModified}`] || {};
                      return !detail.name?.trim() || !detail.email?.trim();
                    }))
              }
              sx={{
                backgroundColor: theme.palette.info.main,
                textTransform: "none",
                color: theme.palette.text.light,
              }}
            >
              {activeTab === "single" ? (
                isUploadingCandidate ? "Uploading..." : "Upload & Continue"
              ) : (
                isProcessing ? (
                  <span className="flex gap-1 text-black items-center">
                    <div className="animate-spin rounded-full border-t-2 border-black h-4 w-4 mr-2"></div>
                    Loading
                  </span>
                ) : (
                  <span className="font-normal flex gap-1 items-center">
                    Submit
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon" className="w-4 h-4"><path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd"></path></svg>
                  </span>
                )
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Upload Tab Component
const SingleUploadTab = ({
  candidateName,
  setCandidateName,
  candidateEmail,
  setCandidateEmail,
  selectedFile,
  setSelectedFile,
  addCandidateFileInputRef,
  handleAddCandidateFileChange,
  handleAddCandidateFileDrop,
  handleAddCandidateFileDragOver,
  theme,
}) => {
  return (
    <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 flex-grow overflow-y-auto">
      {/* Candidate Name Input */}
      <div>
        <label
          htmlFor="candidate-name-tabbed"
          className="block text-sm font-medium mb-2"
          style={{ color: theme.palette.text.main }}
        >
          Candidate Name <span className="text-red-500">*</span>
        </label>
        <input
          id="candidate-name-tabbed"
          type="text"
          value={candidateName}
          onChange={(e) => setCandidateName(e.target.value)}
          placeholder="Enter full name"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{
            borderColor: theme.palette.secondary.v4,
            color: theme.palette.text.main,
          }}
          autoFocus
        />
      </div>

      <div>
        <label
          htmlFor="candidate-email-tabbed"
          className="block text-sm font-medium mb-2"
          style={{ color: theme.palette.text.main }}
        >
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="candidate-email-tabbed"
          type="email"
          value={candidateEmail}
          onChange={(e) => setCandidateEmail(e.target.value)}
          placeholder="Enter email address"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{
            borderColor: theme.palette.secondary.v4,
            color: theme.palette.text.main,
          }}
        />
      </div>

      {/* Upload Resume */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: theme.palette.text.main }}
        >
          Upload resume <span className="text-red-500">*</span>
        </label>
        <input
          ref={addCandidateFileInputRef}
          id="file-input-tabbed"
          type="file"
          onChange={handleAddCandidateFileChange}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        {!selectedFile ? (
          <div
            onDrop={handleAddCandidateFileDrop}
            onDragOver={handleAddCandidateFileDragOver}
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-blue-400"
            style={{
              borderColor: theme.palette.secondary.v4,
            }}
            onClick={() => addCandidateFileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center gap-2">
              <PaperClipIcon className="h-8 w-8" style={{ color: theme.palette.text.main }} />
              <p className="text-sm" style={{ color: theme.palette.text.main }}>
                Add an attachment
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div
              className="flex justify-between items-center p-3 rounded-lg border"
              style={{
                borderColor: theme.palette.secondary.v4,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              <span className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm truncate" style={{ color: theme.palette.text.main }}>
                  {selectedFile.name}
                </span>
                <span className="text-sm whitespace-nowrap" style={{ color: theme.palette.text.secondary }}>
                  ({formatFileSize(selectedFile.size)})
                </span>
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  if (addCandidateFileInputRef.current) {
                    addCandidateFileInputRef.current.value = "";
                  }
                }}
                className="text-red-600 hover:text-red-800 transition-all px-2 flex-shrink-0"
                aria-label="Remove file"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
          </div>
        )}
        <p className="text-xs mt-2" style={{ color: theme.palette.text.secondary }}>
          Allowed: PDF, DOC, DOCX
        </p>
      </div>
    </div>
  );
};

// Bulk Upload Tab Component
const BulkUploadTab = ({ files, bulkDetails = {}, handleBulkDetailChange, handleFileChange, handleRemoveFile, theme }) => {
  const bulkFileInputRef = useRef(null);

  const handleBulkFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      // Create a DataTransfer object to simulate a FileList
      const dataTransfer = new DataTransfer();
      droppedFiles.forEach(file => dataTransfer.items.add(file));
      
      // Create a synthetic event-like object for handleFileChange
      const syntheticEvent = {
        target: {
          files: dataTransfer.files,
        },
      };
      handleFileChange(syntheticEvent);
    }
  };

  const handleBulkFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 flex-grow overflow-y-auto">
      {/* Upload Resume */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: theme.palette.text.main }}
        >
          Upload resume <span className="text-red-500">*</span>
        </label>
        <input
          ref={bulkFileInputRef}
          type="file"
          multiple
          id="fileInputTabbed"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        <div
          onDrop={handleBulkFileDrop}
          onDragOver={handleBulkFileDragOver}
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-blue-400"
          style={{
            borderColor: theme.palette.secondary.v4,
          }}
          onClick={() => bulkFileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center gap-2">
            <PaperClipIcon className="h-8 w-8" style={{ color: theme.palette.text.main }} />
            <p className="text-sm" style={{ color: theme.palette.text.main }}>
              Add an attachment
            </p>
          </div>
        </div>
        <p className="text-xs mt-2" style={{ color: theme.palette.text.secondary }}>
          Allowed: PDF, DOC, DOCX. Max {MAX_FILE_LIMIT} files
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((file) => {
            const fileSize = formatFileSize(file.size);
            const detailKey = `${file.name}-${file.size}-${file.lastModified}`;
            const detail = bulkDetails[detailKey] || { name: "", email: "" };
            const inputClass = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs";
            return (
              <div
                key={detailKey}
                className="p-3 rounded-lg border flex flex-col gap-3"
                style={{
                  borderColor: theme.palette.secondary.v4,
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm truncate" style={{ color: theme.palette.text.main }}>
                      {file.name}
                    </span>
                    <span className="text-sm whitespace-nowrap" style={{ color: theme.palette.text.secondary }}>
                      ({fileSize})
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(file.name);
                    }}
                    className="text-red-600 hover:text-red-800 transition-all px-2 flex-shrink-0"
                    aria-label="Remove file"
                  >
                    <span className="text-xl leading-none">&times;</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label
                      htmlFor={`bulk-name-${detailKey}`}
                      className="block text-xs font-medium mb-2"
                      style={{ color: theme.palette.text.main }}
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`bulk-name-${detailKey}`}
                      type="text"
                      value={detail.name}
                      onChange={(e) => handleBulkDetailChange(file, "name", e.target.value)}
                      placeholder="Enter full name"
                      className={inputClass}
                      style={{
                        borderColor: theme.palette.secondary.v4,
                        color: theme.palette.text.main,
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <label
                      htmlFor={`bulk-email-${detailKey}`}
                      className="block text-xs font-medium mb-2"
                      style={{ color: theme.palette.text.main }}
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`bulk-email-${detailKey}`}
                      type="email"
                      value={detail.email}
                      onChange={(e) => handleBulkDetailChange(file, "email", e.target.value)}
                      placeholder="Enter email address"
                      className={inputClass}
                      style={{
                        borderColor: theme.palette.secondary.v4,
                        color: theme.palette.text.main,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TabbedUploadModal;


